import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { db } from '../../services/firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
// Importar el componente de diagnóstico de notificaciones

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState({});
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [recentEstimates, setRecentEstimates] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    loadDashboardData();
    
    // Refresh data every 30 seconds
    const interval = setInterval(loadDashboardData, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadDashboardData = async () => {
    console.log('Loading admin dashboard data...');
    
    let appointments = [];
    let estimates = [];
    let tickets = [];
    
    try {
      // Load appointments
      const appointmentsRef = collection(db, 'appointments');
      const appointmentsSnapshot = await getDocs(appointmentsRef);
      
      appointmentsSnapshot.forEach((doc) => {
        const data = doc.data();
        appointments.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        });
      });
      
      console.log('Loaded appointments:', appointments.length);
      
      // Load estimates
      const estimatesRef = collection(db, 'estimates');
      const estimatesSnapshot = await getDocs(estimatesRef);
      
      estimatesSnapshot.forEach((doc) => {
        const data = doc.data();
        estimates.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        });
      });
      
      console.log('Loaded estimates:', estimates.length);
      
      // Load support tickets
      try {
        const ticketsRef = collection(db, 'tickets');
        const ticketsSnapshot = await getDocs(ticketsRef);
        
        ticketsSnapshot.forEach((doc) => {
          const data = doc.data();
          tickets.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          });
        });
        
        console.log('Loaded tickets:', tickets.length);
      } catch (ticketsError) {
        console.warn('Error loading tickets:', ticketsError);
        tickets = [];
      }
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
    
    // Sort by creation date (newest first)
    appointments.sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
      return dateB - dateA;
    });
    
    estimates.sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
      return dateB - dateA;
    });
    
    tickets.sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
      return dateB - dateA;
    });
    
    // Calculate metrics
    const pendingAppointments = appointments.filter(apt => apt.status === 'pending').length;
    const confirmedAppointments = appointments.filter(apt => apt.status === 'confirmed').length;
    const completedAppointments = appointments.filter(apt => apt.status === 'completed').length;
    const pendingEstimates = estimates.filter(est => est.status === 'pending').length;
    const quotedEstimates = estimates.filter(est => est.status === 'quoted').length;
    const approvedEstimates = estimates.filter(est => est.status === 'approved').length;
    
    // Get unique clients
    const uniqueClients = new Set([
      ...appointments.map(apt => apt.userId),
      ...estimates.map(est => est.userId),
      ...tickets.map(ticket => ticket.userId)
    ]).size;
    
    setMetrics({
      totalAppointments: appointments.length,
      pendingAppointments,
      confirmedAppointments,
      completedAppointments,
      totalEstimates: estimates.length,
      pendingEstimates,
      quotedEstimates,
      approvedEstimates,
      totalTickets: tickets.length,
      activeClients: uniqueClients
    });
    
    // Set recent data (last 5)
    setRecentAppointments(appointments.slice(0, 5));
    setRecentEstimates(estimates.slice(0, 5));
    setRecentTickets(tickets.slice(0, 5));
  };


  return (
    <Layout>
      
      <div className="analytics-container animate-fade-in-up mb-10" style={{
        padding: windowWidth < 480 ? '0.75rem' : windowWidth < 768 ? '1rem' : '1rem 2rem',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Header Section */}
        <div style={{ 
          marginBottom: windowWidth < 768 ? '1rem' : '2rem',
          maxWidth: '100%',
          overflowX: 'hidden'
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ 
              fontSize: windowWidth < 480 ? '1.25rem' : windowWidth < 768 ? '1.5rem' : '2rem', 
              fontWeight: 'bold', 
              color: '#333', 
              margin: 0,
              wordBreak: 'break-word'
            }}>
              Admin Dashboard
            </h1>
            <p style={{ 
              color: '#666', 
              marginTop: '0.5rem',
              fontSize: windowWidth < 768 ? '0.8rem' : '1rem',
              margin: '0.5rem 0 0 0'
            }}>
              Welcome back, {currentUser?.displayName || 'Admin'}
            </p>
          </div>
        </div>

        {/* Business Metrics Overview */}
        <div className="modern-card" style={{ 
          marginBottom: windowWidth < 768 ? '1.5rem' : '2rem',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: windowWidth < 768 ? 'visible' : 'hidden'
        }}>
          <div className="card-header-modern" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: windowWidth < 768 ? 'flex-start' : 'center',
            flexDirection: windowWidth < 768 ? 'column' : 'row',
            gap: windowWidth < 768 ? '1rem' : '0',
            padding: windowWidth < 480 ? '1rem' : windowWidth < 768 ? '1.25rem' : '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: windowWidth < 768 ? '0.75rem' : '1rem' }}>
              <div style={{
                width: windowWidth < 768 ? '32px' : '40px',
                height: windowWidth < 768 ? '32px' : '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: windowWidth < 768 ? '1rem' : '1.25rem'
              }}>
                📊
              </div>
              <h2 style={{ 
                fontSize: windowWidth < 480 ? '1.1rem' : windowWidth < 768 ? '1.25rem' : '1.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-900)', 
                margin: 0,
                wordBreak: 'break-word'
              }}>
                Business Overview
              </h2>
            </div>
            <button 
              className="btn btn-primary"
              onClick={loadDashboardData}
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: windowWidth < 768 ? '0.75rem 1rem' : '0.75rem 1.5rem',
                fontSize: windowWidth < 768 ? '0.8rem' : '0.875rem',
                width: windowWidth < 768 ? '100%' : 'auto',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none'
              }}
            >
              <span>🔄</span>
              Refresh Data
            </button>
          </div>
        </div>

        {/* Metrics Grid */}
        <div style={{ 
          marginBottom: '2rem',
          display: 'grid', 
          gridTemplateColumns: windowWidth <= 480 ? 'repeat(2, 1fr)' : 
                             windowWidth <= 768 ? 'repeat(2, 1fr)' : 
                             'repeat(4, 1fr)', 
          gap: windowWidth <= 480 ? '0.75rem' : windowWidth <= 768 ? '1rem' : '1.5rem',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          
          {/* Appointments Status */}
          <div className="modern-card" style={{ 
            padding: windowWidth < 480 ? '0.75rem' : '1.5rem'
          }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: 'var(--gray-900)', 
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📅 Appointments Status
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Pending</span>
                <span style={{ fontWeight: '600', color: '#f59e0b' }}>{metrics.pendingAppointments || 0}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Confirmed</span>
                <span style={{ fontWeight: '600', color: '#10b981' }}>{metrics.confirmedAppointments || 0}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Completed</span>
                <span style={{ fontWeight: '600', color: '#3b82f6' }}>{metrics.completedAppointments || 0}</span>
              </div>
            </div>
          </div>

          {/* Estimates Status */}
          <div className="modern-card" style={{ padding: '1.5rem' }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: 'var(--gray-900)', 
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📋 Estimates Status
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Pending</span>
                <span style={{ fontWeight: '600', color: '#f59e0b' }}>{metrics.pendingEstimates || 0}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Quoted</span>
                <span style={{ fontWeight: '600', color: '#8b5cf6' }}>{metrics.quotedEstimates || 0}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Approved</span>
                <span style={{ fontWeight: '600', color: '#10b981' }}>{metrics.approvedEstimates || 0}</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="modern-card" style={{ padding: '1.5rem' }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: 'var(--gray-900)', 
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              ⚡ Quick Actions
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/admin/appointments')}
                style={{ 
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  justifyContent: 'flex-start'
                }}
              >
                📅 Manage Appointments
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => navigate('/admin/estimates')}
                style={{ 
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  justifyContent: 'flex-start'
                }}
              >
                📋 Review Estimates
              </button>
             
            </div>
          </div>
        </div>

        {/* Recent Activity Summary */}
        <div className="modern-card" style={{ 
          marginBottom: windowWidth < 768 ? '1.5rem' : '2rem',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div className="card-header-modern" style={{ 
            padding: windowWidth < 480 ? '1rem' : windowWidth < 768 ? '1.25rem' : '1.5rem'
          }}>
            <h2 style={{ 
              fontSize: windowWidth < 480 ? '1.1rem' : windowWidth < 768 ? '1.25rem' : '1.5rem', 
              fontWeight: '600', 
              color: 'var(--gray-900)', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📈 Recent Activity
            </h2>
          </div>
          
          <div style={{ 
            padding: windowWidth < 480 ? '0.75rem' : windowWidth < 768 ? '1rem' : '1.5rem',
            display: 'grid',
            gridTemplateColumns: windowWidth <= 480 ? '1fr' : windowWidth <= 768 ? '1fr' : 'repeat(3, 1fr)',
            gap: windowWidth <= 480 ? '1.5rem' : windowWidth <= 768 ? '2rem' : '2.5rem'
          }}>
            {/* Recent Appointments */}
            <div>
              <h3 style={{ 
                fontSize: '1rem', 
                fontWeight: '600', 
                color: 'var(--gray-900)', 
                marginBottom: '1rem'
              }}>
                Recent Appointments ({recentAppointments.length})
              </h3>
              {recentAppointments.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  {recentAppointments.slice(0, 3).map((appointment) => (
                    <div key={appointment.id} style={{
                      padding: '1.25rem',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '10px',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)';
                    }}
                    >
                      <div style={{ 
                        fontWeight: '500', 
                        fontSize: '0.875rem',
                        color: 'var(--gray-900)',
                        marginBottom: '0.25rem'
                      }}>
                        {appointment.userName || 'Client'}
                      </div>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: 'var(--gray-600)',
                        marginBottom: '0.25rem'
                      }}>
                        {appointment.service} - {appointment.date}
                      </div>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        backgroundColor: appointment.status === 'pending' ? '#fef3c7' :
                                       appointment.status === 'confirmed' ? '#d1fae5' :
                                       appointment.status === 'completed' ? '#dbeafe' : '#fee2e2',
                        color: appointment.status === 'pending' ? '#d97706' :
                               appointment.status === 'confirmed' ? '#059669' :
                               appointment.status === 'completed' ? '#2563eb' : '#dc2626'
                      }}>
                        {appointment.status?.toUpperCase() || 'PENDING'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  color: 'var(--gray-500)',
                  fontSize: '0.875rem'
                }}>
                  No appointments yet
                </div>
              )}
            </div>

            {/* Recent Estimates */}
            <div>
              <h3 style={{ 
                fontSize: '1rem', 
                fontWeight: '600', 
                color: 'var(--gray-900)', 
                marginBottom: '1rem'
              }}>
                Recent Estimates ({recentEstimates.length})
              </h3>
              {recentEstimates.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  {recentEstimates.slice(0, 3).map((estimate) => (
                    <div key={estimate.id} style={{
                      padding: '1.25rem',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '10px',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)';
                    }}
                    >
                      <div style={{ 
                        fontWeight: '500', 
                        fontSize: '0.875rem',
                        color: 'var(--gray-900)',
                        marginBottom: '0.25rem'
                      }}>
                        {estimate.userName || 'Client'}
                      </div>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: 'var(--gray-600)',
                        marginBottom: '0.25rem'
                      }}>
                        {estimate.service} - {estimate.submittedDate || 'Recent'}
                      </div>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        backgroundColor: estimate.status === 'pending' ? '#fef3c7' :
                                       estimate.status === 'quoted' ? '#ede9fe' :
                                       estimate.status === 'approved' ? '#d1fae5' : '#fee2e2',
                        color: estimate.status === 'pending' ? '#d97706' :
                               estimate.status === 'quoted' ? '#7c3aed' :
                               estimate.status === 'approved' ? '#059669' : '#dc2626'
                      }}>
                        {estimate.status?.toUpperCase() || 'PENDING'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  color: 'var(--gray-500)',
                  fontSize: '0.875rem'
                }}>
                  No estimates yet
                </div>
              )}
            </div>

            {/* Recent Tickets */}
            <div>
              <h3 style={{ 
                fontSize: '1rem', 
                fontWeight: '600', 
                color: 'var(--gray-900)', 
                marginBottom: '1rem'
              }}>
                Recent Tickets ({recentTickets.length})
              </h3>
              {recentTickets.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  {recentTickets.slice(0, 3).map((ticket) => (
                    <div key={ticket.id} style={{
                      padding: '1.25rem',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '10px',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)';
                    }}
                    >
                      <div style={{ 
                        fontWeight: '500', 
                        fontSize: '0.875rem',
                        color: 'var(--gray-900)',
                        marginBottom: '0.25rem'
                      }}>
                        {ticket.userName || 'Client'}
                      </div>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: 'var(--gray-600)',
                        marginBottom: '0.25rem'
                      }}>
                        {ticket.subject || 'Support Request'}
                      </div>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        backgroundColor: ticket.status === 'open' ? '#fef3c7' :
                                       ticket.status === 'in-progress' ? '#dbeafe' :
                                       ticket.status === 'resolved' ? '#d1fae5' : '#fee2e2',
                        color: ticket.status === 'open' ? '#d97706' :
                               ticket.status === 'in-progress' ? '#2563eb' :
                               ticket.status === 'resolved' ? '#059669' : '#dc2626'
                      }}>
                        {ticket.status?.toUpperCase() || 'OPEN'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  color: 'var(--gray-500)',
                  fontSize: '0.875rem'
                }}>
                  No tickets yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
